import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fmt, fmtDate } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { HttpError } from '../../lib/api';
import {
  useApprovePettyCashEntryStep,
  useRejectPettyCashEntryStep,
  useReturnPettyCashEntry,
  useSubmitPettyCashEntry,
} from '../../lib/queries/petty-cash';
import { ApprovalTimeline } from '../approval/ApprovalTimeline';
import { DecisionModal, type Decision } from '../approval/DecisionModal';
import { Can } from '../Can';

import type { EntryWithApproval } from './pc-helpers';

// ============================================================
// Approval panel — petty-cash entry maker-checker (M3, central engine).
// Clone of components/ap/ApBillApprovalPanel adapted to the page's inline-styled
// modal idiom: a maker (pettycash.submit) submits a DRAFT/RETURNED entry, a
// checker (pettycash.approve) approves/returns/rejects the current step via the
// shared DecisionModal. Final-step approval auto-posts the deferred JV server-side.
// ============================================================
export interface PCEntryApprovalPanelProps {
  companyId: string | undefined;
  entry: EntryWithApproval;
  coaName: Map<string, string>;
  onClose: () => void;
}

export function PCEntryApprovalPanel({
  companyId,
  entry,
  coaName,
  onClose,
}: PCEntryApprovalPanelProps) {
  const submitMut = useSubmitPettyCashEntry(companyId);
  const approveMut = useApprovePettyCashEntryStep(companyId);
  const rejectMut = useRejectPettyCashEntryStep(companyId);
  const returnMut = useReturnPettyCashEntry(companyId);

  const [showDecide, setShowDecide] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const qc = useQueryClient();

  const decideBusy = approveMut.isPending || rejectMut.isPending || returnMut.isPending;
  const submitBusy = submitMut.isPending;

  // approvalStatus drives the lifecycle; the accounting `status` is reflected
  // only at terminals (POSTED / REJECTED). A null approvalStatus on a
  // PENDING_APPROVAL row is treated as PENDING so the checker can still act.
  const approvalStatus =
    entry.approvalStatus ?? (entry.status === 'PENDING_APPROVAL' ? 'PENDING' : null);
  const currentStep = entry.currentStep ?? 0;
  // The shared approvals[] rows carry no `id`; the timeline keys off one, so we
  // synthesize a stable per-step key (stepNo is unique within a chain).
  const approvals = useMemo(
    () => (entry.approvals ?? []).map((a) => ({ ...a, id: a.stepNo })),
    [entry.approvals],
  );
  const totalSteps = approvals.length;
  const isPending = approvalStatus === 'PENDING';
  const isResubmittable = approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED';

  async function handleSubmit() {
    setActionError(null);
    try {
      await submitMut.mutateAsync(entry.id);
      onClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
    }
  }

  async function handleDecide(d: Decision) {
    setActionError(null);
    try {
      if (d.action === 'approve') {
        await approveMut.mutateAsync({
          entryId: entry.id,
          step: d.step,
          comment: d.comment,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else if (d.action === 'return') {
        await returnMut.mutateAsync({
          entryId: entry.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      } else {
        await rejectMut.mutateAsync({
          entryId: entry.id,
          step: d.step,
          reason: d.reason,
          ...(d.sodOverrideReason ? { sodOverrideReason: d.sodOverrideReason } : {}),
        });
      }
      setShowDecide(false);
      onClose();
    } catch (e) {
      if (e instanceof HttpError && e.status === 409) {
        setActionError('รายการนี้เปลี่ยนสถานะแล้ว — รีเฟรชข้อมูลให้แล้ว');
        void qc.invalidateQueries({ queryKey: ['pc-entries', companyId] });
      } else {
        setActionError(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด');
      }
    }
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
        }}
        data-testid="pc-approval-panel"
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              สายอนุมัติเงินสดย่อย — {entry.voucherNo}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {entry.description} · {fmt(Number(entry.amount))} บาท ·{' '}
              {coaName.get(entry.categoryCode) ?? entry.categoryCode}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <div
          style={{
            padding: 22,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
          }}
        >
          {/* REJECTED reason banner */}
          {approvalStatus === 'REJECTED' && entry.rejectedReason && (
            <div
              style={{
                background: 'var(--negative-bg, #fdecea)',
                border: '1px solid var(--negative)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-rejected-reason"
            >
              <strong style={{ color: 'var(--negative)' }}>เหตุผลที่ไม่อนุมัติ:</strong>{' '}
              {entry.rejectedReason}
            </div>
          )}

          {/* RETURNED reason banner (resubmit context) */}
          {approvalStatus === 'RETURNED' && entry.returnedReason && (
            <div
              style={{
                background: 'var(--warning-bg, #fef3e8)',
                border: '1px solid var(--warning)',
                borderRadius: 6,
                padding: '10px 14px',
                fontSize: 13,
              }}
              data-testid="pc-returned-reason"
            >
              <strong style={{ color: 'var(--warning)' }}>เหตุผลที่ส่งกลับ:</strong>{' '}
              {entry.returnedReason}
            </div>
          )}

          {/* awaiting-step hint */}
          {isPending && (
            <div className="meta">
              <Icon name="check" size={12} /> รออนุมัติ · ขั้น {currentStep}/{totalSteps}
            </div>
          )}

          <ApprovalTimeline
            approvals={approvals}
            isActive={isPending}
            currentStep={currentStep}
            emptyText="ยังไม่ส่งอนุมัติ"
            leadLine={
              entry.submittedBy ? (
                <div className="meta">
                  ส่งอนุมัติโดย {entry.submittedBy}
                  {entry.submittedAt ? ` · ${fmtDate(entry.submittedAt)}` : ''}
                </div>
              ) : undefined
            }
            trailLine={
              entry.status === 'POSTED' && entry.approvedBy ? (
                <div className="meta">
                  อนุมัติโดย {entry.approvedBy}
                  {entry.approvedAt ? ` · ${fmtDate(entry.approvedAt)}` : ''}
                </div>
              ) : undefined
            }
          />

          {actionError && (
            <div style={{ color: 'var(--negative)', fontSize: 14 }} data-testid="pc-action-error">
              {actionError}
            </div>
          )}
        </div>

        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          {isResubmittable && (
            <Can permission="pettycash.submit">
              <button
                className="btn primary"
                disabled={submitBusy}
                onClick={() => {
                  void handleSubmit();
                }}
              >
                {submitBusy ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
              </button>
            </Can>
          )}
          {isPending && (
            <Can permission="pettycash.approve">
              <button
                className="btn primary"
                disabled={decideBusy}
                onClick={() => setShowDecide(true)}
              >
                พิจารณา
              </button>
            </Can>
          )}
        </div>
      </div>

      {showDecide && (
        <DecisionModal
          voucherNo={entry.voucherNo}
          step={currentStep || 1}
          busy={decideBusy}
          error={actionError}
          titlePrefix="พิจารณารายการเงินสดย่อย"
          onConfirm={(d) => {
            void handleDecide(d);
          }}
          onClose={() => {
            setShowDecide(false);
            setActionError(null);
          }}
        />
      )}
    </div>
  );
}
